import type { JSX } from 'react';
import { useProject, useRuntime, useStudio, useUi, useUiContext } from '../context';
import { summarizeHealth } from '../health/healthModel';
import '../health/health.css';
import { formatDuration, formatKbps } from '../format';
import { useActiveSince, useLiveDuration, useTicker } from '../hooks/useTicker';
import { linkStatusOf } from '../actions/outputs';
import { safely } from '../safe';
import { isDiagnosticsMode } from '../uiStore';
import './shell.css';

function StreamStatus(): JSX.Element {
  const mode = useProject((p) => p.output.streaming.mode);
  const status = useRuntime((r) => linkStatusOf(mode, r));
  const viewers = useRuntime((r) => (mode === 'swarm' ? r.swarm.viewers : r.broadcast.peers.length));
  const live = status === 'live';
  const since = useActiveSince(live || status === 'reconnecting');
  const now = useTicker(since !== null);
  const led = live ? 'live' : status === 'connecting' || status === 'reconnecting' ? 'pending' : '';
  const text =
    status === 'idle' ? 'Offline' : status === 'error' ? 'Stream error' : status === 'connecting' ? 'Connecting…' : status === 'reconnecting' ? 'Reconnecting…' : 'LIVE';
  return (
    <span className={live ? 'item on' : 'item'} role="status" aria-label={`Stream: ${text}`}>
      <span className={`status-led ${led}`} />
      {text}
      {since !== null ? <span className="ui-mono">{formatDuration(now - since)}</span> : null}
      {mode === 'p2p' && status !== 'idle' && status !== 'error' ? <span>· {viewers} connected</span> : null}
      {mode === 'swarm' && status !== 'idle' && status !== 'error' ? <span>· {viewers} watching</span> : null}
    </span>
  );
}

/**
 * Stream health at a glance. While something is live this is never quieter
 * than the worst active issue, and it leads to the Stream Health dock.
 */
function HealthStatus(): JSX.Element {
  const { ui } = useUiContext();
  const health = useRuntime((r) => r.health);
  const summary = summarizeHealth(health);
  const more = summary.count > 1 ? ` (+${summary.count - 1} more)` : '';
  const text = summary.detail ? `${summary.detail}${more}` : summary.label;
  return (
    <button
      type="button"
      className={`statusbar-health ${summary.tone}`}
      data-health={summary.tone}
      aria-label={`Stream health: ${summary.label}${summary.detail ? `. ${summary.detail}${more}` : ''}. Open the Stream Health dock.`}
      title="Open Stream Health"
      onClick={() => ui.getState().dock?.show('health')}
    >
      <span className={`health-dot ${summary.tone}`} aria-hidden="true" />
      {summary.tone === 'warning' || summary.tone === 'critical' ? <strong>{summary.label}</strong> : null}
      <span className="detail">{summary.tone === 'healthy' ? 'Stream healthy' : text}</span>
    </button>
  );
}

function RecordingStatus(): JSX.Element {
  const status = useRuntime((r) => r.recording.status);
  const durationMs = useRuntime((r) => r.recording.durationMs);
  const duration = useLiveDuration(durationMs, status === 'recording');
  const active = status === 'recording' || status === 'paused';
  const led = status === 'recording' ? 'live' : status === 'paused' ? 'paused' : status === 'starting' || status === 'stopping' ? 'pending' : '';
  const text = status === 'recording' ? 'REC' : status === 'idle' ? 'Not recording' : status.charAt(0).toUpperCase() + status.slice(1);
  return (
    <span className={active ? 'item on' : 'item'} role="status" aria-label={`Recording: ${text}`}>
      <span className={`status-led ${led}`} />
      {text}
      {active ? <span className="ui-mono">{formatDuration(duration)}</span> : null}
    </span>
  );
}

function RenderStatus(): JSX.Element {
  const fps = useRuntime((r) => r.render?.fps);
  const target = useRuntime((r) => r.render?.targetFps);
  const skipped = useRuntime((r) => r.render?.skippedFrames);
  const degraded = useRuntime((r) => r.render?.degraded ?? false);
  if (fps === undefined) return <span className="item">Renderer starting…</span>;
  return (
    <span className="item" aria-label="Rendering">
      <span className={`ui-mono ${degraded ? 'ui-warn-text' : ''}`}>
        {fps.toFixed(1)} / {target} fps
      </span>
      <span className="ui-mono">skipped {skipped}</span>
      {degraded ? <span className="ui-warn-text">reduced quality</span> : null}
    </span>
  );
}

function BitrateStatus(): JSX.Element | null {
  const total = useRuntime((r) => {
    let sum = 0;
    for (const stats of Object.values(r.network)) sum += stats.outKbps;
    return Math.round(sum + (r.swarm.stats?.uploadKbps ?? 0));
  });
  const any = useRuntime((r) => Object.keys(r.network).length > 0 || r.swarm.stats !== null);
  if (!any) return null;
  return (
    <span className="item" aria-label="Outgoing bitrate">
      ↑ <span className="ui-mono">{formatKbps(total)}</span>
    </span>
  );
}

function AudioStatus(): JSX.Element {
  const studio = useStudio();
  const state = useRuntime((r) => r.audioContextState);
  if (state === 'running') return <span className="item">Audio on</span>;
  if (state === 'closed') return <span className="item ui-error-text">Audio engine closed</span>;
  return (
    <button type="button" className="statusbar-audio" onClick={() => void safely(studio, () => studio.resumeAudio(), 'Could not start audio')}>
      Audio is paused — click to enable audio
    </button>
  );
}

function SaveStatus(): JSX.Element {
  const dirty = useRuntime((r) => r.collection.dirty);
  const savedAt = useRuntime((r) => r.collection.lastSavedAt);
  const text = dirty ? 'Unsaved changes…' : savedAt ? `Saved ${new Date(savedAt).toLocaleTimeString(undefined, { timeStyle: 'short' })}` : 'Not saved yet';
  return (
    <span className="item" role="status">
      {text}
    </span>
  );
}

export function StatusBar(): JSX.Element {
  const diagnostics = useUi(isDiagnosticsMode);
  return (
    <footer className="statusbar" aria-label="Status">
      <StreamStatus />
      <RecordingStatus />
      <HealthStatus />
      <AudioStatus />
      {diagnostics ? (
        <span className="item ui-warn-text" role="status" aria-label="Diagnostics mode on" title="Diagnostics mode is on: test sources are offered in Add Source">
          DIAG
        </span>
      ) : null}
      <span className="ui-grow" />
      <BitrateStatus />
      <RenderStatus />
      <SaveStatus />
    </footer>
  );
}
